import { useWorkspaceCanvases } from "@/api/workspaces";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { CascadeSub } from "@/object-actions/renderers/DestinationSubmenu";
import type { Canvas, Workspace } from "@cohesive/api-client";
import { Frame, Layers, User } from "lucide-react";
import { useMemo } from "react";
import {
  useLibraryWorkspaces,
  workspaceRowLabel,
} from "./WorkspaceFilePickerSubmenu";

/**
 * "Add canvas" as a nested pop — the canvas twin of the workspace file
 * cascade: workspaces, then each one's canvases; clicking a canvas commits
 * `onPick`. The pick always places a link card (a canvas is never copied
 * into a canvas), and the host canvas is listed but disabled — it can't
 * hold a card of itself.
 */
function DropdownWorkspaceCanvases({
  workspace,
  currentCanvasId,
  onPick,
}: {
  workspace: Workspace;
  currentCanvasId: string;
  onPick: (canvas: Canvas) => void;
}) {
  const { data: canvases = [], isLoading } = useWorkspaceCanvases(workspace.id);
  const sorted = useMemo(
    () =>
      [...canvases].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [canvases],
  );
  const Icon = workspace.is_private ? User : Layers;

  return (
    <CascadeSub
      disabled={!isLoading && sorted.length === 0}
      row={
        <>
          <Icon className="text-foreground/60" />
          <span className="min-w-0 truncate">
            {workspaceRowLabel(workspace)}
          </span>
        </>
      }
    >
      {sorted.length === 0 ? (
        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
          {isLoading ? "Loading…" : "No canvases in this workspace yet."}
        </div>
      ) : (
        sorted.map((canvas) => {
          const isSelf = canvas.id === currentCanvasId;
          return (
            <DropdownMenuItem
              key={canvas.id}
              disabled={isSelf}
              onClick={(e) => {
                e.stopPropagation();
                onPick(canvas);
              }}
            >
              <Frame className="size-4 shrink-0 text-foreground/60" />
              <span className="min-w-0 truncate">
                {canvas.name || "Untitled"}
              </span>
              {isSelf && (
                <span className="ml-auto pl-2 text-xs text-muted-foreground">
                  This canvas
                </span>
              )}
            </DropdownMenuItem>
          );
        })
      )}
    </CascadeSub>
  );
}

/** Dropdown-host variant for the canvas insert and board context menus. */
export function WorkspaceCanvasDropdownCascade({
  workspaceId,
  currentCanvasId,
  onPick,
  label = "Add canvas",
}: {
  /** The host canvas's workspace — listed first. */
  workspaceId: string | undefined;
  currentCanvasId: string;
  onPick: (canvas: Canvas) => void;
  label?: string;
}) {
  const workspaces = useLibraryWorkspaces(workspaceId);

  return (
    <CascadeSub
      disabled={workspaces.length === 0}
      row={
        <>
          <Frame className="size-4 shrink-0 text-foreground/60" />
          <span className="min-w-0 truncate">{label}</span>
        </>
      }
    >
      {workspaces.map((ws) => (
        <DropdownWorkspaceCanvases
          key={ws.id}
          workspace={ws}
          currentCanvasId={currentCanvasId}
          onPick={onPick}
        />
      ))}
    </CascadeSub>
  );
}
